import { UpgradeServiceStep, useUpgradeFlightClass as useStore } from '../store';
import { useSelector } from 'react-redux';
import { getOrder } from '../../Checkout/store/order/selectors';
import { Dispatch, DispatchWithoutAction, useCallback, useMemo } from 'react';
import { useRefundSummary } from '../../RefundSummary';
import { useSelectors } from '../store/selectors';
import {
	CheckinOrderFragment,
	CheckinSaveOrderServicesMutationVariables,
	CheckoutOrderFragment,
	SaveOrderServicesMutationVariables
} from '@websky/graphql';
import { Log } from '../../sentry';
import { useToggleable } from '../../hooks';

export interface UpgradeFlightClassOptions {
	onBackClick: DispatchWithoutAction;
	loading?: boolean;
	onSaveServices: Dispatch<SaveOrderServicesMutationVariables> | Dispatch<CheckinSaveOrderServicesMutationVariables>;
	onContinueWithoutSelection?: DispatchWithoutAction;
	showRefundServicesNotification?: boolean;
}

export const useUpgradeFlightClass = ({
	onBackClick,
	loading,
	onSaveServices,
	onContinueWithoutSelection,
	showRefundServicesNotification
}: UpgradeFlightClassOptions) => {
	// На чекине в этом же срезе стора лежит CheckinOrderFragment, хотя селектор типизирован под checkout
	const order = useSelector(getOrder) as CheckoutOrderFragment | CheckinOrderFragment;

	const { state, dispatch } = useStore(order);

	const {
		isOpen: isRefundNotificationOpen,
		open: openRefundNotification,
		close: closeRefundNotification
	} = useToggleable(false);

	const selectors = useSelectors(state, order, onContinueWithoutSelection);

	const { gdsServicesRefund, hasServicesToRefund, loading: refundServicesLoading } = useRefundSummary(
		selectors.refundSummaryParams
	);

	const handleToggleSegment: Dispatch<string> = segmentId => dispatch({ type: 'toggleSegment', payload: segmentId });
	const handleSetStep: Dispatch<UpgradeServiceStep> = step => dispatch({ type: 'setStep', payload: step });

	const handleGoToSelect = useCallback(() => {
		handleSetStep('select');
	}, []);

	const handleGoToReview = useCallback(() => {
		handleSetStep('review');
	}, [state.selectedSegments]);

	const handleConfirm = useCallback(() => {
		const params = selectors.saveOrderServicesVariables;

		onSaveServices(params as CheckinSaveOrderServicesMutationVariables);
	}, [selectors.saveOrderServicesVariables]);

	const onModalConfirm = () => {
		closeRefundNotification();
		handleConfirm();
	};

	const logWarningStub = useCallback(() => {
		Log({ level: 'warning', message: 'can not calculate next step' });
	}, []);

	const toolbarCallbacks = useMemo(() => {
		if (state.step === 'select') {
			return {
				next: !!state.selectedSegments.length ? handleGoToReview : onContinueWithoutSelection ?? logWarningStub,
				back: onBackClick
			};
		}

		if (showRefundServicesNotification && hasServicesToRefund) {
			return {
				next: openRefundNotification,
				back: handleGoToSelect
			};
		}

		return {
			next: handleConfirm,
			back: handleGoToSelect
		};
	}, [
		state.step,
		state.selectedSegments.length,
		handleGoToSelect,
		handleGoToReview,
		handleConfirm,
		onContinueWithoutSelection,
		onBackClick,
		showRefundServicesNotification,
		hasServicesToRefund,
		openRefundNotification,
		logWarningStub
	]);

	return {
		order: order,
		step: state.step,
		transferDurationBySegments: selectors.transferDurationBySegments,
		refundServicesLoading: refundServicesLoading,
		gdsServicesRefund: gdsServicesRefund,
		toggleSegment: handleToggleSegment,
		service: state.service,
		flights: state.flights,
		selectedSegments: state.selectedSegments,
		nextButtonDisabled: selectors.nextButtonDisabled,
		isServicesSaving: loading,
		toolbarCallbacks,
		totalPrice: state.totalPrice,
		isRefundNotificationOpen,
		onModalConfirm,
		closeRefundNotification
	};
};
